import { reloadAllDatabase } from "@/config/env"
import { and, collection, onSnapshot, Query, query, where } from "firebase/firestore"
import { notifyDocumentAsDeleted } from "../components/global/DeletedDocumentManager"
import { getDeviceId } from "../firebase/deviceId"
import { db } from "../firebase/firebase"
import { queryClient } from "../main"
import { FirestoreDocument } from "../types/firestoreDocument"
import { dexie } from "./localDatabase"
import { logger } from "@/utils/logger"

export const unsubsribersForObservedThings = new Map<string, () => void>()
export const documentConverters = new Map<string, (doc: FirestoreDocument) => FirestoreDocument>()

export function registerFirestoreDocumentConverter<T extends FirestoreDocument>(
   collectionName: string,
   converter: (doc: T) => T,
) {
   documentConverters.set(collectionName, converter as any)
}

/**
 *
 * @param collectionName
 * @param addConstraintOnYoungId the ID of the young to filter by
 * @returns true if the collection is already being observed
 */
export async function startObserveCollection(
   collectionName: string,
   addConstraintOnYoungId?: string,
) {
   if (unsubsribersForObservedThings.has(collectionName)) {
      return true
   }
   logger("Start Observe Collection", collectionName)
   unsubsribersForObservedThings.set(collectionName, () => {})
   const lastUpdateTimestamp =
      (await dexie.table("collection_last_updated_at").get(collectionName))?.last_updated_at || 0

   const collectionRef = collection(db, collectionName)
   let q: Query

   if (addConstraintOnYoungId) {
      q = query(
         collectionRef,
         and(
            where("updated_at", ">", new Date(reloadAllDatabase ? 0 : lastUpdateTimestamp)),
            where("young_id", "==", addConstraintOnYoungId),
         ),
      )
   } else {
      q = query(
         collectionRef,
         where("updated_at", ">", new Date(reloadAllDatabase ? 0 : lastUpdateTimestamp)),
      )
   }

   const deviceId = await getDeviceId()

   try {
      const unsubscribe = onSnapshot(q, (snapshot) => {
         let lastUpdatedTimestamp = lastUpdateTimestamp

         let someDocumentHasBeenUpdatedBySomeoneElse = false

         snapshot.docChanges().forEach((change) => {
            const doc = change.doc
            const data = doc.data()

            logger(`Document ${collectionName}/${doc.id} was ${change.type}`)

            // TODO since we can know removed docs, does is_deleted field make sense?
            if (change.type === "removed" || (data as FirestoreDocument).is_deleted) {
               dexie.table(collectionName).delete(doc.id)
               notifyDocumentAsDeleted(collectionName, doc.id)
            } else if (change.type === "added" || change.type === "modified") {
               // We can have added change for the first time because is doc is added to the snapshot
               let formattedData: FirestoreDocument = {
                  ...data,
                  id: doc.id,
                  created_at: data.created_at?.toDate(),
                  updated_at: data.updated_at?.toDate(),
                  updated_by: data.updated_by,
                  is_deleted: data.is_deleted,
               }

               // Apply custom converter if exists
               const converter = documentConverters.get(collectionName)
               if (converter) {
                  formattedData = converter(formattedData)
               }

               dexie.table(collectionName).put(formattedData)
            }

            const theDocHasBeenUpdatedByCurrentDevice = data.updated_by === deviceId
            const refetchType = theDocHasBeenUpdatedByCurrentDevice ? "active" : "none"
            someDocumentHasBeenUpdatedBySomeoneElse =
               someDocumentHasBeenUpdatedBySomeoneElse || !theDocHasBeenUpdatedByCurrentDevice

            queryClient.invalidateQueries({
               queryKey: ["document", collectionName, doc.id],
               refetchType,
            })

            lastUpdatedTimestamp = Math.max(
               lastUpdatedTimestamp,
               data.updated_at?.toDate()?.valueOf() || 0,
            )
         })

         dexie.table("collection_last_updated_at").put({
            id: collectionName,
            last_updated_at: lastUpdatedTimestamp,
         })

         const refetchType = someDocumentHasBeenUpdatedBySomeoneElse ? "none" : "active"

         queryClient.invalidateQueries({
            queryKey: ["collection", collectionName],
            refetchType,
         })
      })

      unsubsribersForObservedThings.set(collectionName, unsubscribe)
   } catch (e) {
      console.error("Error starting observing collection ", collectionName, e)
   }

   return false
}

export function stopObserveAllRemoteChanges() {
   unsubsribersForObservedThings.forEach((unsubscribe, key) => {
      logger("Stop observing ", key)
      unsubscribe()
   })
   unsubsribersForObservedThings.clear()
}
